import { useIsAppMode } from "@/app-bridge/hooks/useIsAppMode";
import { useMediaPicker } from "@/app-bridge/hooks/useMediaPicker";
import { createFileRoute, Link } from "@tanstack/react-router";
import { IMAGE_ACCEPT } from "@/lib/image-input";
import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  Camera,
  Check,
  ChevronRight,
  Crop,
  Loader2,
  MapPin,
  Save,
  Sparkles,
  Trash2,
  Upload,
  User as UserIcon,
} from "lucide-react";
import { updateMyProfile } from "@/lib/profile.functions";
import { myPlanQuery, myProfileQuery } from "@/lib/queries";
import { PLANS } from "@/lib/plans";
import { adImageSrc } from "@/lib/image-src";
import { AvatarEditor, type AvatarTransform } from "@/components/AvatarEditor";
import { AvatarGallery } from "@/components/AvatarGallery";
import { CameraCaptureDialog } from "@/components/CameraCaptureDialog";
import { useIsMobile } from "@/hooks/use-mobile";
import { PhoneInput, joinPhone, splitPhone } from "@/components/PhoneInput";
import { SignInMethod, useSignInMethods } from "@/components/SignInMethod";
import { checkDisplayName, releaseMyDisplayNameReservation } from "@/lib/profile.functions";
import { VideoScanPromo, PhotoScanPromo } from "@/components/VideoScanPromo";
import { TouchTooltip } from "@/components/ui/touch-tooltip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export const Route = createFileRoute("/_authenticated/profil")({
  head: () => ({
    meta: [
      { title: "Min profil — Sellavo" },
      { name: "description", content: "Ändra ditt visningsnamn och din profilbild på Sellavo." },
      { property: "og:title", content: "Min profil — Sellavo" },
      {
        property: "og:description",
        content: "Ändra ditt visningsnamn och din profilbild på Sellavo.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  loader: async ({ context }) => {
    await Promise.all([
      context.queryClient.ensureQueryData(myProfileQuery()),
      context.queryClient.ensureQueryData(myPlanQuery()),
    ]);
  },
  component: ProfilePage,
});

function ProfilePage() {
  const queryClient = useQueryClient();
  const saveProfile = useServerFn(updateMyProfile);
  const checkName = useServerFn(checkDisplayName);
  const releaseName = useServerFn(releaseMyDisplayNameReservation);

  const { data: profile, isLoading } = useQuery(myProfileQuery());
  const { data: planStatus } = useQuery(myPlanQuery());
  const { hasGoogle, googleAvatarUrl } = useSignInMethods();

  const [displayName, setDisplayName] = useState("");
  const [dial, setDial] = useState("+46");
  const [national, setNational] = useState("");
  const phone = joinPhone(dial, national);
  const [nameCheck, setNameCheck] = useState<{
    state: "idle" | "checking" | "ok" | "taken";
    reason: string | null;
  }>({ state: "idle", reason: null });
  const [avatar, setAvatar] = useState<{
    base64: string;
    mimeType: string;
    transform: AvatarTransform;
  } | null>(null);
  const [originalImage, setOriginalImage] = useState<{ base64: string; mimeType: string } | null>(
    null,
  );
  const [removeAvatar, setRemoveAvatar] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const isMobile = useIsMobile();
  const [preparing, setPreparing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editorSrc, setEditorSrc] = useState<string | null>(null);
  const [editorTransform, setEditorTransform] = useState<AvatarTransform | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  /** true = spara Google-bilden som profilbild när formuläret sparas. */
  const [useGoogle, setUseGoogle] = useState(false);
  const [askGoogle, setAskGoogle] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  /** Regler för namnbyte (kvarvarande byten, aktiva annonser). */
  const nameChange = profile?.nameChange ?? null;
  /** Namnfältet är låst när ett byte inte är tillåtet. */
  const nameLocked = !!nameChange && !nameChange.canChange;
  /** true när användaren faktiskt skrivit in ett nytt namn. */
  const nameDirty =
    !!profile?.displayName &&
    displayName.trim().length > 0 &&
    displayName.trim() !== profile.displayName.trim();

  useEffect(() => {
    if (profile) {
      setDisplayName(profile.displayName);
      const parts = splitPhone(profile.phone);
      setDial(parts.dial);
      setNational(parts.national);
    }
  }, [profile]);

  // Har man Google-bild men ingen profilbild frågar vi en gång om vi får använda den.
  useEffect(() => {
    if (!profile || profile.avatarUrl || !googleAvatarUrl) return;
    let asked = "1";
    try {
      asked = localStorage.getItem("sellavo:google-avatar-asked") ?? "";
    } catch {
      asked = "1";
    }
    if (!asked) setAskGoogle(true);
  }, [profile, googleAvatarUrl]);

  // Kolla namnet mot databasen medan användaren skriver (med liten fördröjning).
  useEffect(() => {
    const name = displayName.trim();
    if (!profile || name === profile.displayName || name.length === 0) {
      setNameCheck({ state: "idle", reason: null });
      // Namnet är tillbaka som det var – håll inte kvar någon tillfällig bokning.
      if (profile) void releaseName({ data: undefined }).catch(() => undefined);
      return;
    }
    setNameCheck({ state: "checking", reason: null });
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const res = await checkName({ data: { displayName: name } });
        if (cancelled) return;
        setNameCheck(
          res.available
            ? { state: "ok", reason: null }
            : { state: "taken", reason: res.reason ?? "Namnet går inte att använda." },
        );
      } catch {
        if (!cancelled) setNameCheck({ state: "idle", reason: null });
      }
    }, 1500);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [displayName, profile, checkName, releaseName]);

  // Lämnar man sidan utan att spara ska namnet bli ledigt för andra direkt.
  const releaseRef = useRef(releaseName);
  releaseRef.current = releaseName;
  const savedNameRef = useRef(false);
  useEffect(
    () => () => {
      if (!savedNameRef.current)
        void releaseRef.current({ data: undefined }).catch(() => undefined);
    },
    [],
  );

  const currentAvatarSrc =
    useGoogle && googleAvatarUrl
      ? googleAvatarUrl
      : avatar
        ? `data:${avatar.mimeType};base64,${avatar.base64}`
        : !removeAvatar && profile?.avatarUrl
          ? adImageSrc(profile.avatarUrl)
          : "";

  const dirty =
    !!profile &&
    (displayName.trim() !== profile.displayName ||
      phone.trim() !== profile.phone ||
      !!avatar ||
      useGoogle ||
      (removeAvatar && !!profile.avatarUrl));

  /** Väljer Google-bilden. Den hämtas och sparas hos oss först när man sparar. */
  const chooseGoogleAvatar = () => {
    setAvatar(null);
    setOriginalImage(null);
    setRemoveAvatar(false);
    setUseGoogle(true);
    setAskGoogle(false);
    try {
      localStorage.setItem("sellavo:google-avatar-asked", "1");
    } catch {
      // Frågan är en bekvämlighet – den får aldrig krascha sidan.
    }
  };

  /** Öppnar redigeraren med originalbilden när den finns, annars den beskurna bilden. */
  const openEditor = () => {
    if (originalImage) {
      setEditorSrc(`data:${originalImage.mimeType};base64,${originalImage.base64}`);
      setEditorTransform(avatar?.transform ?? null);
      return;
    }
    if (!removeAvatar && profile?.avatarOriginalUrl) {
      setEditorSrc(adImageSrc(profile.avatarOriginalUrl));
      setEditorTransform(profile.avatarCrop ?? null);
      return;
    }
    setEditorSrc(currentAvatarSrc);
    setEditorTransform(null);
  };

  const loadImageFile = async (file: File) => {
    setError(null);
    setPreparing(true);
    try {
      const { fileToJpegDataUrl } = await import("@/lib/image-compress");
      // 1024 px räcker gott för en profilbild och gör konverteringen märkbart snabbare.
      const out = await fileToJpegDataUrl(file, 1024, 0.85);
      setOriginalImage({ base64: out.base64, mimeType: out.mimeType });
      setEditorTransform(null);
      setEditorSrc(out.dataUrl);
    } catch {
      setError("Kunde inte läsa bilden. Prova att spara om den som JPG eller PNG.");
    } finally {
      setPreparing(false);
    }
  };

  const { pickImages, sheet: mediaSheet } = useMediaPicker();
  const appMode = useIsAppMode();

  /** I appen öppnar knappen telefonens kamera direkt, annars datorns kamera. */
  const openCamera = async () => {
    if (!appMode) {
      setCameraOpen(true);
      return;
    }
    try {
      setPreparing(true);
      const m = await import("@/app-bridge/core/wrapper");
      const photo = await m.takeNativePhoto();
      if (photo) {
        await loadImageFile(photo);
        return;
      }
    } catch {
      /* faller tillbaka nedan */
    } finally {
      setPreparing(false);
    }
    setCameraOpen(true);
  };


  const pickAvatar = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (inputRef.current) inputRef.current.value = "";
    if (!file) return;
    await loadImageFile(file);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      await saveProfile({
        data: {
          displayName: displayName.trim(),
          phone: phone.trim(),
          ...(avatar
            ? {
                avatarBase64: avatar.base64,
                avatarMimeType: avatar.mimeType,
                avatarCrop: avatar.transform,
                ...(originalImage
                  ? {
                      avatarOriginalBase64: originalImage.base64,
                      avatarOriginalMimeType: originalImage.mimeType,
                    }
                  : {}),
              }
            : {}),
          ...(useGoogle && googleAvatarUrl ? { googleAvatarUrl } : {}),
          ...(removeAvatar && !avatar && !useGoogle ? { removeAvatar: true } : {}),
        },
      });
      setAvatar(null);
      setOriginalImage(null);
      setRemoveAvatar(false);
      setUseGoogle(false);
      savedNameRef.current = true;
      setSaved(true);
      await queryClient.invalidateQueries({ queryKey: ["my-profile"] });
      await queryClient.invalidateQueries({ queryKey: ["contact-defaults"] });
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Något gick fel");
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) {
    return (
      <main className="mx-auto w-full max-w-5xl px-4 py-16 text-muted-foreground sm:px-6 lg:px-8">
        Laddar profil…
      </main>
    );
  }

  return (
    <main className="mx-auto w-full max-w-5xl px-4 sm:px-6 lg:px-8 pb-16 pt-6 sm:pb-20 sm:pt-10">
      <h1 className="font-heading text-3xl font-extrabold tracking-tight text-foreground">
        Min profil
      </h1>
      <p className="mt-2 text-muted-foreground">
        Ditt visningsnamn och din profilbild gäller för alla dina annonser.
      </p>

      <div className="mt-6 space-y-2 rounded-lg border border-border bg-muted/40 p-4">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              E-postadress
            </p>
            <p className="truncate text-sm font-medium text-foreground">{profile?.email}</p>
          </div>
          <span className="shrink-0 rounded-full border border-border bg-background px-2.5 py-1 text-xs text-muted-foreground">
            Kan inte ändras
          </span>
        </div>
        <div className="border-t border-border pt-2">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Inloggning
          </p>
          <SignInMethod className="mt-1.5" />
          {hasGoogle && (
            <p className="mt-1.5 text-xs text-muted-foreground">
              Du loggar in med ditt Google-konto — du behöver inget lösenord här.
            </p>
          )}
        </div>
        <div className="flex items-center justify-between gap-3 border-t border-border pt-2">
          <p className="text-sm text-muted-foreground">
            Konto: {planStatus ? PLANS[planStatus.plan].name : "…"}
          </p>
          <Button asChild variant="outline" size="sm">
            <a href="/priser">Se konton</a>
          </Button>
        </div>
      </div>

      <Link
        to="/adresser"
        className="mt-3 flex items-center justify-between gap-3 rounded-lg border border-border bg-card p-4 transition hover:bg-muted/60"
      >
        <span className="flex items-center gap-3">
          <MapPin className="h-5 w-5 text-primary" />
          <span>
            <span className="block text-sm font-medium text-foreground">Mina adresser</span>
            <span className="block text-xs text-muted-foreground">
              Spara hemadress och hämtplatser för dina annonser
            </span>
          </span>
        </span>
        <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
      </Link>

      <form onSubmit={submit} className="mt-6 space-y-6">
        <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
          <div className="flex flex-col items-center gap-5 sm:flex-row sm:items-center">
            <div className="relative shrink-0">
              <div className="rounded-full bg-gradient-to-br from-primary/70 to-accent/70 p-[3px]">
                <div className="flex h-28 w-28 items-center justify-center overflow-hidden rounded-full border-4 border-card bg-muted">
                  {currentAvatarSrc ? (
                    <img
                      src={currentAvatarSrc}
                      alt="Din profilbild"
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <UserIcon className="h-10 w-10 text-muted-foreground" />
                  )}
                </div>
              </div>
            </div>

            <div className="min-w-0 flex-1 text-center sm:text-left">
              <Label className="text-base">Profilbild</Label>
              <p className="mt-1 text-sm text-muted-foreground">
                Kvadratisk bild som visas vid dina annonser och i chatten. Du kan flytta, zooma och
                rotera den innan du sparar.
              </p>
              <div className="mt-3 flex items-center gap-2">
                <TouchTooltip text="Ladda upp en bild från din telefon eller dator">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={preparing}
                    onClick={() =>
                      pickImages({
                        onFiles: (files) => {
                          const file = files[0];
                          if (file) void loadImageFile(file);
                        },
                        openWebPicker: () => inputRef.current?.click(),
                      })
                    }
                  >
                    {preparing ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Upload className="h-4 w-4" />
                    )}
                    <span className="hidden sm:inline">
                      {preparing ? "Förbereder…" : currentAvatarSrc ? "Byt bild" : "Välj bild"}
                    </span>
                  </Button>
                </TouchTooltip>
                {(!isMobile || appMode) && (
                  <TouchTooltip
                    text={appMode ? "Ta ett kort med kameran" : "Ta ett kort med datorns kamera"}
                  >
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={preparing}
                      onClick={() => void openCamera()}
                    >
                      <Camera className="h-4 w-4" />
                      <span className="hidden sm:inline">Kamera</span>
                    </Button>
                  </TouchTooltip>
                )}
                <TouchTooltip text="Välj en färdig avatar">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setGalleryOpen(true)}
                  >
                    <Sparkles className="h-4 w-4" />
                    <span className="hidden sm:inline">Avatar</span>
                  </Button>
                </TouchTooltip>

                {googleAvatarUrl && (
                  <TouchTooltip text="Använd bilden från ditt Google-konto">
                    <Button
                      type="button"
                      variant={useGoogle ? "default" : "outline"}
                      size="sm"
                      onClick={chooseGoogleAvatar}
                    >
                      <img
                        src={googleAvatarUrl}
                        alt=""
                        className="h-4 w-4 rounded-full object-cover"
                      />
                      <span className="hidden sm:inline">Google</span>
                    </Button>
                  </TouchTooltip>
                )}
                {currentAvatarSrc && (
                  <TouchTooltip text="Flytta, zooma och rotera bilden">
                    <Button type="button" variant="outline" size="sm" onClick={openEditor}>
                      <Crop className="h-4 w-4" />
                      <span className="hidden sm:inline">Justera</span>
                    </Button>
                  </TouchTooltip>
                )}
                {currentAvatarSrc && (
                  <TouchTooltip text="Ta bort profilbilden">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label="Ta bort profilbilden"
                      className="ml-auto text-destructive hover:text-destructive"
                      onClick={() => setConfirmRemove(true)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TouchTooltip>
                )}
              </div>
            </div>
          </div>
          {mediaSheet}
          <input
            ref={inputRef}
            type="file"
            accept={IMAGE_ACCEPT}
            className="hidden"
            onChange={pickAvatar}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="displayName">Visningsnamn</Label>
          <Input
            id="displayName"
            required
            maxLength={40}
            placeholder="T.ex. Karl eller Karl J."
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            disabled={nameLocked}
          />
          {nameCheck.state === "checking" && (
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" /> Kollar om namnet är ledigt…
            </p>
          )}
          {nameCheck.state === "ok" && (
            <p className="flex items-center gap-1.5 text-xs text-primary">
              <Check className="h-3 w-3" /> Namnet är ledigt
            </p>
          )}
          {nameCheck.state === "taken" && (
            <p className="text-xs text-destructive">{nameCheck.reason}</p>
          )}
          <p className="text-xs text-muted-foreground">
            Namnet köpare ser på alla dina annonser. Vi rekommenderar förnamn eller alias – skriv
            inte ut efternamn.
          </p>

          {/* Regler för namnbyte – tryggheten bygger på att namn är stabila. */}
          {nameChange && (
            <div
              className={`rounded-xl border p-3 text-xs leading-relaxed ${
                nameChange.canChange
                  ? "border-border bg-muted/40 text-muted-foreground"
                  : "border-amber-400/60 bg-amber-50 text-amber-900 dark:bg-amber-950/40 dark:text-amber-200"
              }`}
            >
              {nameChange.activeAdCount > 0 ? (
                <p>
                  <strong>Du kan inte byta visningsnamn just nu.</strong> Du har{" "}
                  {nameChange.activeAdCount} publicerad
                  {nameChange.activeAdCount === 1 ? " annons" : "e annonser"}. Pausa eller avsluta{" "}
                  {nameChange.activeAdCount === 1 ? "den" : "dem"} under{" "}
                  <Link to="/mina-annonser" className="font-semibold underline">
                    Mina annonser
                  </Link>{" "}
                  först – annars blir det förvirrande för köpare mitt i en affär.
                </p>
              ) : nameChange.remaining === 0 ? (
                <p>
                  <strong>Inga byten kvar.</strong> {nameChange.reason}
                </p>
              ) : nameChange.firstWeek ? (
                <p>
                  <strong>Din första vecka:</strong> du får göra {nameChange.remaining} byte av
                  visningsnamn nu om något blev fel. Därefter gäller max 2 byten per 12 månader.
                </p>
              ) : (
                <p>
                  <strong>
                    Byten kvar: {nameChange.remaining} av {nameChange.max}.
                  </strong>{" "}
                  Du kan byta visningsnamn max 2 gånger per 12 månader, och bara när du inte har
                  några publicerade annonser.
                </p>
              )}
              <p className="mt-1.5">
                När du byter låser vi ditt gamla namn i 180 dagar så att ingen annan kan ta det, och
                köpare du pratar med ser i 90 dagar att du hette något annat tidigare.
              </p>
            </div>
          )}

          {nameDirty && nameChange?.canChange && (
            <p className="rounded-lg border border-amber-400/60 bg-amber-50 p-2.5 text-xs font-medium text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
              Du är på väg att byta visningsnamn. Det räknas som ett av dina byten och går inte att
              ångra direkt.
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="phone">Telefonnummer</Label>
          <PhoneInput
            id="phone"
            dial={dial}
            national={national}
            onDialChange={setDial}
            onNationalChange={setNational}
          />
          <p className="text-xs text-muted-foreground">
            Bara du ser ditt nummer. Det visas aldrig i annonser – köpare kontaktar dig via
            meddelanden på Sellavo.
          </p>
        </div>

        {error && (
          <p className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </p>
        )}

        <Button
          type="submit"
          disabled={
            !dirty || saving || nameCheck.state === "taken" || nameCheck.state === "checking"
          }
          className="w-full"
        >
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> Sparar…
            </>
          ) : saved ? (
            <>
              <Check className="h-4 w-4" /> Sparat
            </>
          ) : dirty ? (
            <>
              <Save className="h-4 w-4" /> Spara ändringar
            </>
          ) : (
            "Inga ändringar gjorda"
          )}
        </Button>
      </form>

      <VideoScanPromo className="mt-10" />
      <PhotoScanPromo className="mt-4" />

      <AvatarEditor
        open={!!editorSrc}
        src={editorSrc}
        initialTransform={editorTransform}
        onCancel={() => setEditorSrc(null)}
        onSave={(result) => {
          setAvatar(result);
          setRemoveAvatar(false);
          setEditorSrc(null);
        }}
      />

      <CameraCaptureDialog
        open={cameraOpen}
        onOpenChange={setCameraOpen}
        title="Ta en profilbild"
        description="Titta in i kameran och tryck på Ta kort. Sedan kan du flytta, zooma och beskära bilden."
        onCapture={(file) => void loadImageFile(file)}
      />

      <AvatarGallery
        open={galleryOpen}
        onOpenChange={setGalleryOpen}
        onPick={(result) => {
          setAvatar({ ...result, transform: { x: 0, y: 0, zoom: 1, rotation: 0 } });
          setOriginalImage(result);
          setEditorTransform(null);
          setRemoveAvatar(false);
        }}
      />

      <AlertDialog open={confirmRemove} onOpenChange={setConfirmRemove}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ta bort profilbilden?</AlertDialogTitle>
            <AlertDialogDescription>
              Din profilbild försvinner från dina annonser och chattar. Du kan ladda upp en ny när
              som helst. Ändringen sparas när du klickar på Spara ändringar.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setAvatar(null);
                setOriginalImage(null);
                setRemoveAvatar(true);
              }}
            >
              Ta bort bilden
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={askGoogle} onOpenChange={setAskGoogle}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Vill du använda din Google-bild?</AlertDialogTitle>
            <AlertDialogDescription>
              Du är inloggad med Google och har ingen profilbild här. Säger du ja hämtar och sparar
              vi bilden från ditt Google-konto som profilbild. Du kan byta bild när du vill, och
              alltid välja Google-bilden igen.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                try {
                  localStorage.setItem("sellavo:google-avatar-asked", "1");
                } catch {
                  // Ignorera – frågan är bara en bekvämlighet.
                }
              }}
            >
              Nej tack
            </AlertDialogCancel>
            <AlertDialogAction onClick={chooseGoogleAvatar}>Ja, använd den</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </main>
  );
}
